import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LogoWord } from "./Logo";
import { NewsletterSignup } from "./NewsletterSignup";
import { ThemeQuickSwitcher } from "./ThemeQuickSwitcher";
import { AdRail } from "./AdRail";
import { Button } from "./ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Menu, X, LogOut, User as UserIcon, Sparkles } from "lucide-react";
import LiveTicker from "./LiveTicker";

const navItems = [
    { to: "/", label: "Fact-Check", end: true },
    { to: "/today", label: "Today" },
    { to: "/trending", label: "Trending" },
    { to: "/donate", label: "Donate" },
    { to: "/docs/api", label: "API" },
    { to: "/pricing", label: "Pro" },
    { to: "/about", label: "Story" },
    { to: "/themes", label: "Studio" },
    { to: "/transparency", label: "Impact" },
];

export function Layout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    return (
        <div className="min-h-screen flex flex-col">
            <header className="glass-nav sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                    <Link to="/" data-testid="header-logo-link" className="flex items-center">
                        <LogoWord />
                    </Link>
                    <nav className="hidden md:flex items-center gap-1">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}-link`}
                                className={({ isActive }) =>
                                    `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                        isActive
                                            ? "bg-white/5 text-accent"
                                            : "text-muted-foreground hover:text-accent hover:bg-white/5"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="hidden md:flex items-center gap-3">
                        <ThemeQuickSwitcher />
                        {user ? (
                            <>
                                {user.is_pro && (
                                    <span
                                        data-testid="header-pro-badge"
                                        className="hidden lg:inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-primary/20 text-accent border border-primary/40"
                                    >
                                        <Sparkles className="h-3 w-3" /> PRO
                                    </span>
                                )}
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            className="rounded-full h-9 pl-2 pr-4"
                                            data-testid="header-user-menu-trigger"
                                        >
                                            <span className="w-7 h-7 rounded-full bg-primary/40 border border-primary/60 flex items-center justify-center text-xs font-bold text-accent overflow-hidden">
                                                {user.picture ? (
                                                    <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                                                ) : (
                                                    user.name?.[0]?.toUpperCase() || "U"
                                                )}
                                            </span>
                                            <span className="ml-2 text-sm">{user.name?.split(" ")[0]}</span>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="min-w-52 bg-card border-border">
                                        <DropdownMenuLabel className="text-xs text-muted-foreground">
                                            {user.email}
                                        </DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem asChild data-testid="menu-dashboard-item">
                                            <Link to="/dashboard" className="cursor-pointer">
                                                <UserIcon className="h-4 w-4 mr-2" /> Dashboard
                                            </Link>
                                        </DropdownMenuItem>
                                        {!user.is_pro && (
                                            <DropdownMenuItem asChild data-testid="menu-upgrade-item">
                                                <Link to="/pricing" className="cursor-pointer">
                                                    <Sparkles className="h-4 w-4 mr-2" /> Upgrade to Pro
                                                </Link>
                                            </DropdownMenuItem>
                                        )}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onClick={handleLogout}
                                            className="cursor-pointer text-destructive"
                                            data-testid="menu-logout-item"
                                        >
                                            <LogOut className="h-4 w-4 mr-2" /> Sign out
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="ghost"
                                    onClick={() => navigate("/login")}
                                    className="text-sm"
                                    data-testid="header-login-button"
                                >
                                    Sign in
                                </Button>
                                <Button
                                    onClick={() => navigate("/signup")}
                                    className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold"
                                    data-testid="header-signup-button"
                                >
                                    Get started
                                </Button>
                            </>
                        )}
                    </div>
                    <button
                        className="md:hidden text-accent p-2"
                        onClick={() => setMobileOpen((v) => !v)}
                        data-testid="mobile-menu-toggle"
                        aria-label="Toggle menu"
                    >
                        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
                {mobileOpen && (
                    <div className="md:hidden border-t border-white/10 bg-[hsl(var(--background))]/95 backdrop-blur-lg">
                        <div className="px-6 py-4 flex flex-col gap-2">
                            {navItems.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={item.end}
                                    onClick={() => setMobileOpen(false)}
                                    className={({ isActive }) =>
                                        `px-3 py-2 rounded-lg text-sm font-medium ${
                                            isActive ? "bg-white/5 text-accent" : "text-muted-foreground"
                                        }`
                                    }
                                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}-link`}
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                            <div className="pt-2 flex flex-col gap-2">
                                {user ? (
                                    <>
                                        <Button onClick={() => { setMobileOpen(false); navigate("/dashboard"); }} variant="secondary">
                                            Dashboard
                                        </Button>
                                        <Button onClick={handleLogout} variant="ghost">Sign out</Button>
                                    </>
                                ) : (
                                    <>
                                        <Button onClick={() => { setMobileOpen(false); navigate("/login"); }} variant="secondary">
                                            Sign in
                                        </Button>
                                        <Button
                                            onClick={() => { setMobileOpen(false); navigate("/signup"); }}
                                            className="bg-primary text-primary-foreground"
                                        >
                                            Get started
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </header>

            <LiveTicker />

            <main className="flex-1">{children}</main>

            {/* Sponsored ad rails — hidden on Pro, on select pages, and on <2xl screens */}
            <AdRail side="left" />
            <AdRail side="right" />

            <footer className="border-t border-white/10 mt-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
                    <div className="col-span-2 md:col-span-2">
                        <LogoWord />
                        <p className="text-sm text-muted-foreground mt-4 max-w-md leading-relaxed">
                            Real-Check is a mission-driven AI truth engine — fact-checks claims and detects scams.
                            Every dollar from direct donations funds the <span className="text-emerald-400 font-semibold">Hands of Hope</span> verified charity foundation.
                        </p>
                        <div className="mt-5 flex flex-col gap-2 max-w-sm">
                            <NewsletterSignup source="footer" compact />
                        </div>
                    </div>
                    <div>
                        <h4 className="text-accent font-semibold text-sm mb-3">Product</h4>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                            <li><Link to="/" className="hover:text-accent">Fact-check</Link></li>
                            <li><Link to="/today" className="hover:text-accent">Fact of the Day</Link></li>
                            <li><Link to="/trending" className="hover:text-accent">Trending</Link></li>
                            <li><Link to="/pricing" className="hover:text-accent">Pro</Link></li>
                            <li><Link to="/docs/api" className="hover:text-accent" data-testid="footer-api-link">API</Link></li>
                            <li><Link to="/developers" className="hover:text-accent" data-testid="footer-developers-link">Developer console</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="text-accent font-semibold text-sm mb-3">Mission</h4>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                            <li><Link to="/donate" className="hover:text-accent">Donate</Link></li>
                            <li><Link to="/wall" className="hover:text-accent">Donor wall</Link></li>
                            <li><Link to="/transparency" className="hover:text-accent">Where money goes</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="text-accent font-semibold text-sm mb-3">Project</h4>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                            <li><Link to="/about" className="hover:text-accent" data-testid="footer-about-link">The story</Link></li>
                            <li><Link to="/transparency" className="hover:text-accent">About Hands of Hope</Link></li>
                            <li><Link to="/wall" className="hover:text-accent">Donor wall</Link></li>
                            <li><Link to="/terms" className="hover:text-accent" data-testid="footer-terms-link">Terms</Link></li>
                            <li><Link to="/privacy" className="hover:text-accent" data-testid="footer-privacy-link">Privacy</Link></li>
                        </ul>
                    </div>
                </div>
                <div className="border-t border-white/5 py-8 text-center text-xs text-muted-foreground space-y-2">
                    <p data-testid="footer-copyright">
                        © 2025–{new Date().getFullYear()} Real-Check.org — All Rights Reserved.
                    </p>
                    <p data-testid="footer-creator" className="text-muted-foreground/90">
                        Created by <span className="text-accent font-semibold">Chris</span>, Disabled Veteran &amp; Founder of{" "}
                        <span className="text-emerald-400">Hands of Hope</span> (2025). Dedicated to my mama and Charlie.
                    </p>
                    <p className="text-muted-foreground/70">
                        <a href="https://real-check.org" className="text-primary hover:underline">
                            real-check.org
                        </a>
                    </p>
                </div>
            </footer>
        </div>
    );
}
